import { RefObject, useRef, useState } from 'react'
import { v4 as uuid } from 'uuid'
import Konva from 'konva'
import { VisualShape } from 'app/frontend/types/custom/VisualPlan'
import {
  DEFAULT_STROKE_COLOR,
  DEFAULT_STROKE_WIDTH,
  ShapeStyle,
} from '../constants'
import { nextShapeName } from '../shapes/shapeLabels'
import { getContentPointer } from './getContentPointer'

type Params = {
  stageRef: RefObject<Konva.Stage | null>
  width: number
  height: number
  shapes: VisualShape[]
  onChange: (shapes: VisualShape[]) => void
  onSelectIds: (ids: string[]) => void
  getStyle: () => ShapeStyle
}

export const usePencilDrawing = ({
  stageRef,
  width,
  height,
  shapes,
  onChange,
  onSelectIds,
  getStyle,
}: Params) => {
  const drawingRef = useRef(false)
  const pointsRef = useRef<number[]>([])
  const movedRef = useRef(false)
  const [draftPoints, setDraftPoints] = useState<number[]>([])

  const isActive = () => drawingRef.current

  const startDrawing = () => {
    const pos = getContentPointer(stageRef.current)
    if (!pos) return
    drawingRef.current = true
    movedRef.current = false
    pointsRef.current = [pos.x, pos.y, pos.x, pos.y]
    setDraftPoints(pointsRef.current)
  }

  const draw = () => {
    if (!drawingRef.current) return
    const pos = getContentPointer(stageRef.current)
    if (!pos) return
    movedRef.current = true
    pointsRef.current = [...pointsRef.current, pos.x, pos.y]
    setDraftPoints(pointsRef.current)
  }

  const endDrawing = () => {
    if (!drawingRef.current) return
    drawingRef.current = false
    const pts = pointsRef.current
    const moved = movedRef.current
    pointsRef.current = []
    movedRef.current = false
    setDraftPoints([])
    if (!moved || width === 0 || height === 0) return

    let minX = Infinity
    let maxX = -Infinity
    let minY = Infinity
    let maxY = -Infinity
    for (let i = 0; i < pts.length; i += 2) {
      const x = pts[i]
      const y = pts[i + 1]
      if (x < minX) minX = x
      if (x > maxX) maxX = x
      if (y < minY) minY = y
      if (y > maxY) maxY = y
    }
    const centerX = (minX + maxX) / 2
    const centerY = (minY + maxY) / 2
    const normalizedPoints = pts.map((point, i) =>
      i % 2 === 0 ? (point - centerX) / width : (point - centerY) / height,
    )

    const style = getStyle()
    const shape: VisualShape = {
      id: uuid(),
      type: 'pencil',
      x: centerX / width,
      y: centerY / height,
      points: normalizedPoints,
      stroke_rgb: style.stroke_rgb ?? DEFAULT_STROKE_COLOR,
      stroke_width: style.stroke_width ?? DEFAULT_STROKE_WIDTH,
      name: nextShapeName(shapes, 'pencil'),
    }
    onChange([...shapes, shape])
    onSelectIds([shape.id])
  }

  return { draftPoints, isActive, startDrawing, draw, endDrawing }
}
